import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
    useReactTable,
    getCoreRowModel,
    getSortedRowModel,
    flexRender,
} from '@tanstack/react-table';
import {
    MagnifyingGlassIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    ArrowUpIcon,
    ArrowDownIcon,
    ArrowsUpDownIcon,
    UserGroupIcon,
    PlusIcon,
    BuildingOffice2Icon,
    EnvelopeIcon,
    PhoneIcon,
    MapPinIcon,
    IdentificationIcon,
} from '@heroicons/react/24/outline';
import { subAgencyAPI, addressAPI } from '../../api/api';
import { salesApiService } from '../../api/salesApi';
import SubAgencyFormModal from './SubAgencyFormModal';
import SalesCallModal from '../../components/modals/SalesCallModal';
import CreatableSelect from 'react-select/creatable';
import ActionToolbar from '../../components/ui/ActionToolbar';
import IconActionButton from '../../components/ui/IconActionButton';
import FilterPanel from '../../components/ui/FilterPanel';
import FilterActions from '../../components/ui/FilterActions';

const SubAgencyPage = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [globalFilter, setGlobalFilter] = useState('');
    const [debouncedFilter, setDebouncedFilter] = useState('');
    const [countryFilter, setCountryFilter] = useState('');
    const [cityFilter, setCityFilter] = useState('');
    const [isFiltersOpen, setIsFiltersOpen] = useState(true);
    const [sorting, setSorting] = useState([]);

    const [currentPage, setCurrentPage] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const PAGE_SIZE = 50;
    const [addresses, setAddresses] = useState([]);
    const [filterOptions, setFilterOptions] = useState({ countries: [], cities: [] });
    const [filterRows, setFilterRows] = useState([]);
    const [isSubAgencyModalOpen, setIsSubAgencyModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [selectedSalesCall, setSelectedSalesCall] = useState(null);
    const [isSalesCallModalOpen, setIsSalesCallModalOpen] = useState(false);
    const [refreshSalesCallsTrigger, setRefreshSalesCallsTrigger] = useState(0);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedFilter(globalFilter);
            setCurrentPage(1);
        }, 400);
        return () => clearTimeout(timer);
    }, [globalFilter]);

    useEffect(() => {
        setCurrentPage(1);
    }, [countryFilter, cityFilter]);

    const fetchSubAgencies = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await subAgencyAPI.getSubAgencies({
                page: currentPage,
                page_size: PAGE_SIZE,
                search: debouncedFilter,
                country: countryFilter,
                city: cityFilter,
            });

            if (response.success) {
                setRows(response.sub_agencies || []);
                setTotalRecords(response.total || 0);
                setTotalPages(response.total_pages || 1);
            } else {
                setRows(response.data?.sub_agencies || response.sub_agencies || []);
                setTotalRecords(response.data?.total || response.total || 0);
                setTotalPages(response.data?.total_pages || response.total_pages || 1);
            }
        } catch {
            setError('Sunucu bağlantı hatası.');
            setRows([]);
            setTotalRecords(0);
            setTotalPages(1);
        } finally {
            setLoading(false);
        }
    }, [currentPage, debouncedFilter, countryFilter, cityFilter]);

    useEffect(() => {
        fetchSubAgencies();
    }, [fetchSubAgencies]);

    useEffect(() => {
        const fetchAddresses = async () => {
            try {
                const response = await addressAPI.getAddresses({ page_size: 1000 });
                const list = response?.data?.addresses || response?.addresses || [];
                setAddresses(Array.isArray(list) ? list : []);
            } catch (addressError) {
                console.error('Address list fetch error:', addressError);
                setAddresses([]);
            }
        };

        fetchAddresses();
    }, []);

    useEffect(() => {
        const fetchFilterOptions = async () => {
            try {
                const response = await subAgencyAPI.getSubAgencies({ page: 1, page_size: 1000 });
                const list = response?.data?.sub_agencies || response?.sub_agencies || [];
                const normalized = Array.isArray(list) ? list : [];

                const countries = [...new Set(
                    normalized.map((item) => (item.company_country || '').trim()).filter(Boolean)
                )].sort((a, b) => a.localeCompare(b, 'tr'));

                const cities = [...new Set(
                    normalized.map((item) => (item.company_city || '').trim()).filter(Boolean)
                )].sort((a, b) => a.localeCompare(b, 'tr'));

                setFilterOptions({ countries, cities });
                setFilterRows(normalized);
            } catch (filterError) {
                console.error('Sub agency filter options error:', filterError);
                setFilterOptions({ countries: [], cities: [] });
                setFilterRows([]);
            }
        };

        fetchFilterOptions();
    }, []);

    const openEditModal = (item) => {
        setEditingItem(item);
        setIsSubAgencyModalOpen(true);
    };

    const openCreateModal = () => {
        setEditingItem(null);
        setIsSubAgencyModalOpen(true);
    };

    const handleSaveSubAgency = async (payload) => {
        try {
            if (editingItem?.id) {
                await subAgencyAPI.updateSubAgency(editingItem.id, payload);
            } else {
                await subAgencyAPI.createSubAgency(payload);
            }

            setIsSubAgencyModalOpen(false);
            setEditingItem(null);
            await fetchSubAgencies();
        } catch (saveError) {
            alert(saveError.message || 'Sub agency kaydedilemedi.');
        }
    };

    const handleOpenSalesCall = (call) => {
        setSelectedSalesCall(call);
        setIsSalesCallModalOpen(true);
    };

    const handleSaveSalesCall = async (formData, selectedFiles) => {
        try {
            let response;
            let salesCallId;

            if (selectedSalesCall?.id) {
                response = await salesApiService.updateSalesCall(selectedSalesCall.id, formData);
                salesCallId = selectedSalesCall.id;
            } else {
                response = await salesApiService.createSalesCall(formData);
                salesCallId = response?.id ?? response?.data?.id;
            }

            if (response?.success) {
                if (selectedFiles?.length > 0 && salesCallId) {
                    const fileData = new FormData();
                    selectedFiles.forEach((file) => fileData.append('files', file));
                    await salesApiService.uploadSalesCallAttachments(salesCallId, fileData);
                }
                setIsSalesCallModalOpen(false);
                setSelectedSalesCall(null);
                setRefreshSalesCallsTrigger((t) => t + 1);
            } else {
                alert(response?.message || 'Kayıt sırasında hata oluştu.');
            }
        } catch (err) {
            console.error(err);
            alert('Sunucu hatası.');
        }
    };

    const handleDeleteAttachment = async (attachmentId) => {
        if (!window.confirm('Bu dosyayı silmek istediğinize emin misiniz?')) return;
        try {
            const response = await salesApiService.deleteSalesCallAttachment(attachmentId);
            if (response?.success) {
                if (selectedSalesCall) {
                    setSelectedSalesCall((prev) =>
                        prev ? { ...prev, attachments: (prev.attachments || []).filter((a) => a.id !== attachmentId) } : null
                    );
                }
            } else {
                alert(response?.message || 'Dosya silinemedi.');
            }
        } catch (err) {
            alert('Dosya silinirken hata.');
        }
    };

    const countryOptions = useMemo(
        () => filterOptions.countries.map((country) => ({ value: country, label: country })),
        [filterOptions.countries]
    );

    const cityOptions = useMemo(() => {
        const source = countryFilter
            ? filterRows.filter((item) => (item.company_country || '').trim() === countryFilter)
            : filterRows;

        const cities = [...new Set(
            source.map((item) => (item.company_city || '').trim()).filter(Boolean)
        )].sort((a, b) => a.localeCompare(b, 'tr'));

        return cities.map((city) => ({ value: city, label: city }));
    }, [countryFilter, filterRows]);

    const columns = useMemo(() => [
        {
            accessorKey: 'name',
            header: 'YETKİLİ',
            cell: ({ row }) => (
                <span className="font-semibold text-slate-900 dark:text-white">
                    {`${row.original.name || ''} ${row.original.last_name || ''}`.trim() || '—'}
                </span>
            ),
        },
        {
            accessorKey: 'company_name',
            header: 'ALT ACENTE / ŞUBE',
            cell: ({ getValue }) =>
                getValue() ? (
                    <span className="inline-flex items-center gap-1.5 text-slate-700 dark:text-slate-300">
                        <BuildingOffice2Icon className="w-3.5 h-3.5 shrink-0" />
                        {getValue()}
                    </span>
                ) : (
                    <span className="text-slate-400">—</span>
                ),
        },
        {
            accessorKey: 'company_city',
            header: 'ŞEHİR',
            cell: ({ getValue }) =>
                getValue() ? (
                    <span className="inline-flex items-center gap-1.5 text-slate-600 dark:text-slate-400">
                        <MapPinIcon className="w-3.5 h-3.5 shrink-0" />
                        {getValue()}
                    </span>
                ) : (
                    <span className="text-slate-400">—</span>
                ),
        },
        {
            accessorKey: 'company_country',
            header: 'ÜLKE',
            cell: ({ getValue }) => <span className="text-slate-600 dark:text-slate-400">{getValue() || '—'}</span>,
        },
        {
            accessorKey: 'phone',
            header: 'TELEFON',
            cell: ({ getValue }) =>
                getValue() ? (
                    <span className="inline-flex items-center gap-1.5 text-slate-600 dark:text-slate-400">
                        <PhoneIcon className="w-3.5 h-3.5 shrink-0" />
                        {getValue()}
                    </span>
                ) : (
                    <span className="text-slate-400">—</span>
                ),
        },
        {
            accessorKey: 'email',
            header: 'E-POSTA',
            cell: ({ getValue }) =>
                getValue() ? (
                    <a
                        href={`mailto:${getValue()}`}
                        className="inline-flex items-center gap-1.5 text-blue-600 dark:text-blue-400 hover:underline"
                    >
                        <EnvelopeIcon className="w-3.5 h-3.5 shrink-0" />
                        {getValue()}
                    </a>
                ) : (
                    <span className="text-slate-400">—</span>
                ),
        },
        {
            accessorKey: 'transfer_id',
            header: 'TRANSFER ID',
            cell: ({ getValue }) =>
                getValue() ? (
                    <span className="inline-flex items-center gap-1.5 text-slate-600 dark:text-slate-400">
                        <IdentificationIcon className="w-3.5 h-3.5 shrink-0" />
                        {getValue()}
                    </span>
                ) : (
                    <span className="text-slate-400">—</span>
                ),
        },
    ], []);

    const table = useReactTable({
        data: rows,
        columns,
        state: { sorting },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        manualPagination: true,
    });

    const SortIcon = ({ column }) => {
        const sorted = column.getIsSorted();
        if (sorted === 'asc') return <ArrowUpIcon className="w-3 h-3 text-blue-600" />;
        if (sorted === 'desc') return <ArrowDownIcon className="w-3 h-3 text-blue-600" />;
        return <ArrowsUpDownIcon className="w-3 h-3 text-slate-300 group-hover:text-slate-500 transition-colors" />;
    };

    return (
        <div className="p-6 space-y-5 max-w-[1600px] mx-auto">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/30">
                        <UserGroupIcon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">Sub Agencies</h1>
                        <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">Alt acente ve şube rehberi</p>
                    </div>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-slate-100 dark:bg-slate-800 rounded-xl text-sm font-semibold text-slate-600 dark:text-slate-300">
                    Toplam
                    <span className="text-blue-600 dark:text-blue-400 font-black text-base">{totalRecords}</span>
                    kayıt
                </div>
            </div>

            {error && (
                <div className="px-4 py-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-sm text-red-600 dark:text-red-400 font-medium">
                    {error}
                </div>
            )}

            <ActionToolbar>
                <IconActionButton icon={PlusIcon} title="Yeni Kayıt" onClick={openCreateModal} />
            </ActionToolbar>

            <div className="space-y-4">
                <FilterPanel
                    title="Filters"
                    isOpen={isFiltersOpen}
                    onToggle={() => setIsFiltersOpen((prev) => !prev)}
                >
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                        <div className="relative md:col-span-2">
                            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <input
                                type="text"
                                placeholder="Yetkili, şube, şehir veya e-posta ile ara..."
                                value={globalFilter}
                                onChange={(e) => setGlobalFilter(e.target.value)}
                                className="w-full pl-9 pr-4 py-2 text-sm bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-white placeholder:text-slate-400 transition-all"
                            />
                        </div>
                        <CreatableSelect
                            className="react-select-container text-xs"
                            classNamePrefix="react-select"
                            options={countryOptions}
                            value={countryOptions.find((option) => option.value === countryFilter) || null}
                            onChange={(selected) => {
                                setCountryFilter(selected?.value || '');
                                setCityFilter('');
                            }}
                            placeholder="Tum ulkeler"
                            isClearable
                            isSearchable
                            formatCreateLabel={(inputValue) => `"${inputValue}" ara...`}
                            onCreateOption={() => {}}
                            styles={{
                                control: (base) => ({
                                    ...base,
                                    minHeight: '38px',
                                }),
                                menu: (base) => ({
                                    ...base,
                                    zIndex: 40,
                                }),
                            }}
                            classNames={{
                                control: () => 'bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg',
                                singleValue: () => 'text-slate-900 dark:text-white',
                                input: () => 'text-slate-900 dark:text-white',
                                placeholder: () => 'text-slate-400 dark:text-slate-500',
                                menu: () => 'bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg mt-1 shadow-lg',
                                option: (state) =>
                                    `cursor-pointer px-3 py-2 ${state.isFocused ? 'bg-blue-50 dark:bg-slate-800' : ''} ${state.isSelected ? 'bg-blue-600 text-white' : 'text-slate-900 dark:text-slate-100'}`
                            }}
                        />
                        <div className="flex items-center gap-2">
                            <CreatableSelect
                                className="react-select-container text-xs w-full"
                                classNamePrefix="react-select"
                                options={cityOptions}
                                value={cityOptions.find((option) => option.value === cityFilter) || null}
                                onChange={(selected) => setCityFilter(selected?.value || '')}
                                placeholder="Tum sehirler"
                                isClearable
                                isSearchable
                                formatCreateLabel={(inputValue) => `"${inputValue}" ara...`}
                                onCreateOption={() => {}}
                                styles={{
                                    control: (base) => ({
                                        ...base,
                                        minHeight: '38px',
                                    }),
                                    menu: (base) => ({
                                        ...base,
                                        zIndex: 40,
                                    }),
                                }}
                                classNames={{
                                    control: () => 'bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg',
                                    singleValue: () => 'text-slate-900 dark:text-white',
                                    input: () => 'text-slate-900 dark:text-white',
                                    placeholder: () => 'text-slate-400 dark:text-slate-500',
                                    menu: () => 'bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg mt-1 shadow-lg',
                                    option: (state) =>
                                        `cursor-pointer px-3 py-2 ${state.isFocused ? 'bg-blue-50 dark:bg-slate-800' : ''} ${state.isSelected ? 'bg-blue-600 text-white' : 'text-slate-900 dark:text-slate-100'}`
                                }}
                            />
                            <FilterActions
                                onSearch={() => {
                                    setDebouncedFilter(globalFilter);
                                    setCurrentPage(1);
                                }}
                                onClear={() => {
                                    setGlobalFilter('');
                                    setDebouncedFilter('');
                                    setCountryFilter('');
                                    setCityFilter('');
                                }}
                            />
                        </div>
                    </div>
                </FilterPanel>

                <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
                <div className="overflow-x-auto relative">
                    {loading && (
                        <div className="absolute inset-0 z-10 bg-white/70 dark:bg-slate-900/70 backdrop-blur-[2px] flex items-center justify-center">
                            <div className="flex flex-col items-center gap-2">
                                <div className="w-7 h-7 border-[3px] border-blue-600 border-t-transparent rounded-full animate-spin" />
                                <span className="text-xs font-bold text-blue-600 uppercase tracking-widest">Yükleniyor</span>
                            </div>
                        </div>
                    )}

                    <table className="w-full text-sm text-left">
                        <thead>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <tr key={headerGroup.id} className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400 text-[11px] font-bold uppercase tracking-wider">
                                    {headerGroup.headers.map((header) => (
                                        <th
                                            key={header.id}
                                            className="px-5 py-3.5 border-b border-slate-100 dark:border-slate-800 cursor-pointer select-none group hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors whitespace-nowrap"
                                            onClick={header.column.getToggleSortingHandler()}
                                        >
                                            <div className="flex items-center gap-1.5">
                                                {flexRender(header.column.columnDef.header, header.getContext())}
                                                <SortIcon column={header.column} />
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                        <tbody className="divide-y divide-slate-50 dark:divide-slate-800/80">
                            {table.getRowModel().rows.length === 0 && !loading ? (
                                <tr>
                                    <td colSpan={columns.length} className="px-5 py-16 text-center text-slate-400 dark:text-slate-600">
                                        <UserGroupIcon className="w-10 h-10 mx-auto mb-3 opacity-30" />
                                        <p className="font-semibold text-sm">Kayıt bulunamadı</p>
                                        {globalFilter && (
                                            <p className="text-xs mt-1">
                                                "<span className="text-blue-500">{globalFilter}</span>" araması için sonuç yok.
                                            </p>
                                        )}
                                    </td>
                                </tr>
                            ) : (
                                table.getRowModel().rows.map((row) => (
                                    <tr
                                        key={row.id}
                                        className="hover:bg-blue-50/40 dark:hover:bg-blue-900/10 transition-colors cursor-pointer"
                                        onDoubleClick={() => openEditModal(row.original)}
                                        title="Düzenlemek için çift tıklayın"
                                    >
                                        {row.getVisibleCells().map((cell) => (
                                            <td key={cell.id} className="px-5 py-3.5 dark:text-slate-300">
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </td>
                                        ))}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="px-5 py-3.5 border-t border-slate-100 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-800/40 flex items-center justify-between gap-4">
                    <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
                        Sayfa <span className="text-slate-900 dark:text-white font-bold">{currentPage}</span> / {totalPages}
                        <span className="ml-2 text-slate-400">({totalRecords} toplam kayıt)</span>
                    </span>
                    <div className="flex items-center gap-2">
                        <button
                            disabled={currentPage <= 1 || loading}
                            onClick={() => setCurrentPage((p) => p - 1)}
                            className="p-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                            <ChevronLeftIcon className="w-4 h-4" />
                        </button>
                        <button
                            disabled={currentPage >= totalPages || loading}
                            onClick={() => setCurrentPage((p) => p + 1)}
                            className="p-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                            <ChevronRightIcon className="w-4 h-4" />
                        </button>
                    </div>
                </div>
                </div>
            </div>

            <SubAgencyFormModal
                isOpen={isSubAgencyModalOpen}
                onClose={() => {
                    setIsSubAgencyModalOpen(false);
                    setEditingItem(null);
                }}
                onSave={handleSaveSubAgency}
                editingItem={editingItem}
                addresses={addresses}
                onOpenSalesCall={handleOpenSalesCall}
                refreshSalesCallsTrigger={refreshSalesCallsTrigger}
            />

            <SalesCallModal
                isOpen={isSalesCallModalOpen}
                onClose={() => {
                    setIsSalesCallModalOpen(false);
                    setSelectedSalesCall(null);
                    setRefreshSalesCallsTrigger((t) => t + 1);
                }}
                onSave={handleSaveSalesCall}
                onDeleteAttachment={handleDeleteAttachment}
                initialData={selectedSalesCall}
                availableCalls={[]}
            />
        </div>
    );
};

export default SubAgencyPage;
